// pages/support/sms/NewSmsPage.tsx

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, X, User, Users, MessageSquare, AlertCircle, CheckCircle, RefreshCw } from "lucide-react";
import { smsService } from "../../../services/smsService";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import PageHeader from "../../../components/ui/PageHeader";

export default function NewSmsPage() {
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    to: "",
    recipients: "",
    message: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.message.trim()) {
      alertError("Le message est requis");
      return;
    }

    const recipientsList = formData.recipients
      ? formData.recipients
          .split(",")
          .map((r) => r.trim())
          .filter((r) => r)
      : [];

    if (!formData.to && recipientsList.length === 0) {
      alertError("Au moins un destinataire est requis");
      return;
    }

    setSending(true);
    try {
      const data: any = {
        message: formData.message,
      };

      if (formData.to) {
        data.to = formData.to;
      }

      if (recipientsList.length > 0) {
        data.recipients = recipientsList;
      }

      await smsService.sendSms(data);
      alertSuccess("SMS envoyé avec succès");
      navigate(-1);
    } catch (error: any) {
      const message = error.response?.data?.message || "Erreur lors de l'envoi du SMS";
      alertError(message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <PageHeader title="Nouveau SMS" subtitle="Envoyez un SMS à vos clients" />

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center gap-2">
                <User size={16} className="text-gray-400" />
                Destinataire
              </div>
            </label>
            <input
              type="text"
              name="to"
              value={formData.to}
              onChange={handleChange}
              placeholder="+33 6 12 34 56 78"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">Ou utilisez le champ ci-dessous pour plusieurs</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-gray-400" />
                Destinataires multiples
              </div>
            </label>
            <input
              type="text"
              name="recipients"
              value={formData.recipients}
              onChange={handleChange}
              placeholder="+33 6 12 34 56 78, +33 6 87 65 43 21"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">Séparez les numéros par des virgules</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center gap-2">
                <MessageSquare size={16} className="text-gray-400" />
                Message <span className="text-red-500">*</span>
              </div>
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={5}
              maxLength={1600}
              placeholder="Rédigez votre message..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>1600 caractères max</span>
              <span>{formData.message.length}/1600</span>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-600">
              Les SMS sont envoyés via Twilio. Assurez-vous d'avoir un solde suffisant.
            </p>
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={sending}
              className="flex-1 px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {sending ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Envoyer
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>

      {/* Aperçu du message */}
      {formData.message && (
        <div className="mt-6 bg-gray-50 rounded-xl border border-gray-200 p-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Aperçu du message</p>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{formData.message}</p>
            {formData.to && <p className="text-xs text-gray-400 mt-2">Envoyé à: {formData.to}</p>}
            {formData.recipients && <p className="text-xs text-gray-400 mt-1">Envoyé à: {formData.recipients}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
